/**
 * NWF-174: header phone contact (see snippets/nwf-header-phone.liquid).
 *
 * Two parts:
 *
 * 1. <nwf-header-phone> — the desktop pill with its hover flyout. The pill is a
 *    real tel: link; the flyout opens on hover (200ms close delay so the pointer
 *    can travel down into it), on keyboard focus, and closes on Escape, on focus
 *    leaving, or on a click elsewhere. On touch devices at desktop width the
 *    first tap only opens the flyout, the second tap dials - a phone call
 *    should never start from an accidental tap.
 *
 * 2. Swipe-to-close for the mobile bottom sheet. The sheet itself is the theme's
 *    <drawer-component> (open/close, focus trap, Escape, overlay tap all come
 *    from theme.js); this only adds the drag gesture on the handle and panel.
 */
(() => {
    // The theme editor re-injects this script on every header section reload.
    // The custom element definition and the document listeners from the first
    // run stay valid, so any later run is a no-op (same guard as nwf-cart-staffel.js).
    if (window.__nwfHeaderPhoneInit) return;
    window.__nwfHeaderPhoneInit = true;

    const CLOSE_DELAY = 200;
    const SWIPE_CLOSE_DISTANCE = 80;

    if (!customElements.get('nwf-header-phone')) {
        customElements.define(
            'nwf-header-phone',
            class NwfHeaderPhone extends HTMLElement {
                connectedCallback() {
                    this.trigger = this.querySelector('[data-nwf-phone-trigger]');
                    this.flyout = this.querySelector('[data-nwf-phone-flyout]');
                    if (!this.trigger || !this.flyout) return;

                    this.closeTimer = null;
                    this.abortController = new AbortController();
                    const { signal } = this.abortController;

                    this.addEventListener('pointerenter', this.onPointerEnter.bind(this), { signal });
                    this.addEventListener('pointerleave', this.onPointerLeave.bind(this), { signal });
                    this.addEventListener('focusin', this.open.bind(this), { signal });
                    this.addEventListener('focusout', this.onFocusOut.bind(this), { signal });
                    this.trigger.addEventListener('pointerdown', this.onTriggerPointerDown.bind(this), { signal });
                    this.trigger.addEventListener('click', this.onTriggerClick.bind(this), { signal });
                    document.addEventListener('keydown', this.onKeyDown.bind(this), { signal });
                    document.addEventListener('pointerdown', this.onDocumentPointerDown.bind(this), { signal });
                }

                disconnectedCallback() {
                    this.abortController?.abort();
                    window.clearTimeout(this.closeTimer);
                }

                get isOpen() {
                    return this.classList.contains('is-open');
                }

                open() {
                    window.clearTimeout(this.closeTimer);
                    if (this.isOpen) return;
                    this.classList.add('is-open');
                    this.trigger.setAttribute('aria-expanded', 'true');
                }

                close() {
                    window.clearTimeout(this.closeTimer);
                    if (!this.isOpen) return;
                    this.classList.remove('is-open');
                    this.trigger.setAttribute('aria-expanded', 'false');
                }

                scheduleClose() {
                    window.clearTimeout(this.closeTimer);
                    this.closeTimer = window.setTimeout(() => this.close(), CLOSE_DELAY);
                }

                onPointerEnter(event) {
                    if (event.pointerType === 'touch') return;
                    this.open();
                }

                onPointerLeave(event) {
                    if (event.pointerType === 'touch') return;
                    this.scheduleClose();
                }

                onFocusOut(event) {
                    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
                    this.close();
                }

                onTriggerPointerDown(event) {
                    // Remember what kind of pointer started this tap and whether the
                    // flyout was already open before it. Decided here, not in click:
                    // focusin or synthetic hover events may open the flyout between
                    // pointerdown and click, which would otherwise look like a
                    // second tap. matchMedia('(hover: none)') is not used because
                    // iPadOS reports hover support inconsistently.
                    this.tapPointerType = event.pointerType;
                    this.tapStartedOpen = this.isOpen;
                }

                onTriggerClick(event) {
                    const isTouchTap = this.tapPointerType === 'touch' || this.tapPointerType === 'pen';
                    this.tapPointerType = null;
                    if (!isTouchTap) return;

                    // Touch: first tap opens, only a tap on the already open pill dials.
                    if (!this.tapStartedOpen) {
                        event.preventDefault();
                        this.open();
                    }
                }

                onKeyDown(event) {
                    if (event.key !== 'Escape' || !this.isOpen) return;
                    this.close();
                    this.trigger.focus();
                }

                onDocumentPointerDown(event) {
                    if (this.isOpen && !this.contains(event.target)) this.close();
                }
            },
        );
    }

    /**
     * Swipe down on the sheet panel to close it. The panel follows the finger
     * (transition off while dragging) and either snaps back or calls the
     * drawer's own hide(), so focus restore and body unlock stay with theme.js.
     */
    const initSheetSwipe = (sheet) => {
        const panel = sheet.querySelector('[data-nwf-phone-sheet-inner]');
        if (!panel || panel.dataset.nwfSwipeReady) return;
        panel.dataset.nwfSwipeReady = 'true';

        let startY = null;
        let deltaY = 0;

        const reset = () => {
            panel.classList.remove('is-dragging');
            panel.style.transform = '';
            startY = null;
            deltaY = 0;
        };

        panel.addEventListener(
            'touchstart',
            (event) => {
                // Only start a drag from the top of the panel (handle + title area),
                // so hours and links below keep normal scroll and selection.
                if (event.touches.length !== 1) return;
                if (!event.target.closest('.nwf-header-phone__handle, .nwf-header-phone__sheet-title')) return;
                startY = event.touches[0].clientY;
                deltaY = 0;
                panel.classList.add('is-dragging');
            },
            { passive: true },
        );

        panel.addEventListener(
            'touchmove',
            (event) => {
                if (startY === null) return;
                deltaY = Math.max(0, event.touches[0].clientY - startY);
                panel.style.transform = `translate3d(0, ${deltaY}px, 0)`;
            },
            { passive: true },
        );

        const onTouchEnd = () => {
            if (startY === null) return;
            const shouldClose = deltaY > SWIPE_CLOSE_DISTANCE;
            reset();
            if (shouldClose && typeof sheet.hide === 'function') sheet.hide();
        };

        panel.addEventListener('touchend', onTouchEnd);
        panel.addEventListener('touchcancel', onTouchEnd);
    };

    const initSheets = () => {
        document.querySelectorAll('.nwf-header-phone__sheet').forEach(initSheetSwipe);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initSheets, { once: true });
    } else {
        initSheets();
    }

    // The header section is re-rendered in the theme editor.
    document.addEventListener('shopify:section:load', initSheets);
})();
